import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';
import { GuideAction } from '@/components/guides/GuideAction';
import { GuideScreenshot } from '@/components/guides/GuideScreenshot';

export function ScheduleInterviewSection() {
  return (
    <GuideSection
      id="schedule-interview"
      eyebrow="Interviews"
      title="Schedule an interview"
      description="The 3-step wizard issues a license, generates a coaching prompt, and emails the candidate — all in one flow."
    >
      <GuideScreenshot
        src="/guides/schedule-wizard-step1.png"
        alt="Schedule Interview wizard, step 1 of 3 (Details). Candidate summary card at top, then Company + Role fields, Interview date picker, Internal notes textarea"
        caption="Step 1 — Details. The candidate is pre-selected if you opened the wizard from their card."
      />

      <GuideScreenshot
        src="/guides/schedule-wizard-step2-uploaded.png"
        alt="Schedule Interview wizard, step 2 of 3 (Materials) with resume and JD files uploaded, each showing a green check icon"
        caption="Step 2 — Materials. Green checks confirm the files parsed successfully. The wizard waits for AI parsing before advancing."
      />

      <GuideScreenshot
        src="/guides/schedule-wizard-step3-confirm.png"
        alt="Schedule Interview wizard, step 3 of 3 (Confirm). Summary of candidate, company, role, date, notes, and materials, with a 'What happens next' info box"
        caption="Step 3 — Confirm. Review everything, then click Schedule interview to issue the license, generate the prompt, and email the candidate."
      />

      <GuideScreenshot
        src="/guides/schedule-ai-training.png"
        alt="Schedule wizard confirmation state with button changed to 'AI training in process…' while the license is being issued and the prompt generated"
        caption="While the prompt generator runs you'll see 'AI training in process…' on the submit button. Typically 10–60 seconds."
      />

      <GuideScreenshot
        src="/guides/schedule-success-license.png"
        alt="Success screen showing 'Interview scheduled', a masked license key (NRX-••••...), reveal + copy buttons, and an 'Email sent to candidate' confirmation"
        caption="Success screen — the license key is masked by default. Click the eye icon to reveal, or the copy icon to grab it. The candidate already got an email with the same key."
      />

      <GuideSteps>
        <GuideStep number={1} title="Open the wizard">
          <p>
            From <strong>Interviews</strong> click <em>"Schedule interview"</em>, or jump straight
            from a candidate card on the Candidates page (click the <em>Interview</em>{' '}
            quick-action).
          </p>
          <GuideAction href="/interviews">Open Interviews</GuideAction>
        </GuideStep>

        <GuideStep number={2} title="Step 1 — Details">
          <p>Fill in:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <strong>Candidate</strong> — pick from your roster (or the candidate is preselected if
              you came from their card).
            </li>
            <li>
              <strong>Company name</strong> — the employer the candidate is interviewing with (e.g.,
              Google).
            </li>
            <li>
              <strong>Position title</strong> — the role (e.g., "SDE II").
            </li>
            <li>
              <strong>Interview date</strong> — when the interview happens. Used for scheduling and
              display only; the candidate can start earlier or later.
            </li>
            <li>
              <strong>Internal notes</strong> (optional) — what the recruiter wants the coach to
              emphasise. The candidate doesn't see these.
            </li>
          </ul>
        </GuideStep>

        <GuideStep number={3} title="Step 2 — Materials">
          <p>
            Upload the <strong>resume</strong> (required, PDF/DOC) and the{' '}
            <strong>job description</strong> (either a file or 30+ characters of pasted text).
          </p>
          <p>
            You'll see a progress bar as Gemini parses the materials — the wizard waits for parsing
            to finish before it lets you advance. Each file shows a status: parsing, parsed, or
            failed. Re-upload a file if it fails.
          </p>
          <p>
            Optional: add <strong>recruiter notes</strong> on what the candidate needs to focus on.
            These feed directly into the generated coaching prompt.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Step 3 — Confirm">
          <p>
            Review the summary. Click <strong>Schedule interview</strong>. In a few seconds the
            portal:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              Creates the session row in <code>neurax.interview_sessions</code>.
            </li>
            <li>
              Issues (or reuses) a <strong>NeuraX license</strong> for this candidate.
            </li>
            <li>
              Auto-generates a coaching prompt from the parsed materials using our top Claude Opus
              model.
            </li>
            <li>
              Sends a <strong>branded email</strong> to the candidate with their license key, the
              desktop app download link, and next steps.
            </li>
          </ul>
          <p>
            You land on a success screen showing the license key (masked, reveal and copy buttons
            available) and an email-sent confirmation.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideScreenshot
        src="/guides/licenses-list.png"
        alt="NeuraX Licenses page showing one license for a candidate: ACTIVE badge, UNBOUND badge, masked license key, issue date, last used, valid until, and license type B2B_SEAT"
        caption="Every license you issue shows up on the NeuraX Licenses page. ACTIVE means usable; UNBOUND means the candidate hasn't activated it on a machine yet."
      />

      <GuideCallout variant="info" title="Licenses are automatic">
        <p>
          You don't issue licenses from a separate button. The wizard checks if the candidate
          already has a license; if not, it mints a fresh <code>NRX-XXXXXX-XXXXXXXX</code> key. If
          they already have one, it's reused so the candidate doesn't have to re-enter anything.
        </p>
      </GuideCallout>

      <GuideCallout variant="tip" title="If the email fails">
        <p>
          The success screen shows a warning if email dispatch failed (e.g., bounced address). The
          session still exists and the license is still valid — just copy the license key from the
          success screen and send it to the candidate manually.
        </p>
      </GuideCallout>

      <GuideCallout variant="warning" title="Pending state">
        <p>
          Sessions start as <strong>pending</strong> and flip to <strong>active</strong> the moment
          the candidate opens their NeuraX app and connects. If a session stays pending for hours,
          check with the candidate — they may need help installing the app.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
